/**
 * One live session, driven by whoever is looking at it.
 *
 * The lobby screen and the diagnostics panel both call this and both read the same
 * [lobbyState](./lobbyState.ts). Neither owns a transport, so opening the panel over a running
 * lobby shows that lobby rather than starting a second session.
 *
 * The transport is injected. That is what lets the panel drive a loopback pair, and what will let a
 * test drive a whole lobby without a browser.
 *
 * Once a game starts, the running mode needs the same callbacks the lobby is on. Rather than open a
 * second connection, `relayTo` hands a second set of listeners the same events: the lobby keeps
 * tracking who is here, and the mode gets the peers and the bytes. There is one transport per
 * session and there is never a moment where two things believe they own it.
 *
 * There can be several relays at once. The host's seat keeping and the running mode both want the
 * same events, and a relay that replaced its predecessor would silently switch one of them off. So
 * a relay is added to a set and removed by the stop function it was handed.
 *
 * State and notices are kept apart on purpose. State is what is true now, and a screen renders it
 * whenever it likes. A notice is something that happened once, so it is delivered once: folding
 * "player three left" into the state would mean inventing an identity for each line so a screen
 * could tell a new one from the one it already showed.
 */

import { createCarriedToken } from '@/net/session/carriedToken';
import { createLobbyGuest } from '@/net/lobby/lobbyGuest';
import { createLobbyHost } from '@/net/lobby/lobbyHost';
import { EMPTY_LOBBY, reduceLobby } from '@/net/lobby/lobbyState';
import { runRejoinAttempt } from '@/net/lobby/rejoinAttempt';
import { SEAT_HOLD_MS } from '@/net/session/sessionLimits';
import { asNetError } from '@/net/session/sessionErrors';
import { mintRejoinToken } from '@/net/session/rejoinToken';
import { openTrysteroTransport } from '@/net/trysteroTransport';
import type { CarriedToken } from '@/net/session/carriedToken';
import type { LobbyEvent, LobbyState } from '@/net/lobby/lobbyState';
import type { LobbyGuest } from '@/net/lobby/lobbyGuest';
import type { LobbyHost } from '@/net/lobby/lobbyHost';
import type { NetError } from '@/net/session/sessionErrors';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { SeatDirectory } from '@/net/session/seatDirectory';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';
import type { SessionRules } from '@/net/session/sessionRules';
import type { LinkState } from '@/net/session/netSession';
import type {
  PeerId,
  SessionRole,
  Transport,
  TransportFactory,
  TransportListeners,
} from '@/net/transportContract';

/** The clock and the timer, injected so a whole session runs in a test without a browser. */
export interface LobbySessionClock {
  readonly nowMs: () => number;
  readonly repeat: (everyMs: number, run: () => void) => () => void;
  readonly sleep: (ms: number) => Promise<void>;
}

export const BROWSER_LOBBY_CLOCK: LobbySessionClock = {
  nowMs: (): number => performance.now(),
  repeat: (everyMs: number, run: () => void): (() => void) => {
    const timer = setInterval(run, everyMs);
    return (): void => {
      clearInterval(timer);
    };
  },
  sleep: (ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    }),
};

export interface LobbySession {
  state(): LobbyState;
  /** Returns the unsubscribe function, in the same shape as `InputSource.onEngagedChange`. */
  subscribe(listener: (state: LobbyState) => void): () => void;
  /** One line at a time, as things happen to other people. Delivered once, never replayed. */
  onNotice(listener: (notice: SessionNotice) => void): () => void;
  /** Hosts a session. A code is generated unless one is given, which only the panel does. */
  host(code?: string | null): Promise<void>;
  join(code: string): Promise<void>;
  leave(): Promise<void>;
  /** Says this machine is ready. Nothing on the host, which is never waiting for itself. */
  setReady(ready: boolean): void;
  /**
   * Says what this machine is called, before a session exists and after every re-roll.
   *
   * Kept here rather than passed to `host` or `join`, because a name outlives one connection: a
   * guest that reconnects announces the name it has now, on whatever transport it lands on.
   */
  setName(name: string): void;
  /**
   * Says who this machine is playing as, for exactly the same reason and with the same lifetime.
   *
   * The host builds this player's body from it, so a guest that never says has no body: this is the
   * one thing a surface has to hand over before a game is worth starting.
   */
  setSheet(sheet: CharacterSheet): void;
  /** Sets how the room is played, and tells the room. Nothing on a guest: the host decides. */
  setRules(rules: SessionRules): void;
  /**
   * Tries to get back into the session this machine was just in, for a bounded while.
   *
   * Only a guest has anything to get back into. Resolves either way: the state says whether it
   * worked, the same as opening does.
   */
  rejoin(): Promise<void>;
  /** Stops a rejoin that is still running, for a player who would rather go back to the menu. */
  cancelRejoin(): void;
  /** The live transport, for whatever ends up sending game bytes over it. */
  transport(): Transport | null;
  /** The host's peer id on the live connection, or null when there is none. */
  hostId(): PeerId | null;
  /**
   * Says when the connection goes and when the next one lands.
   *
   * A rejoin replaces the transport under everything holding this session, the running game
   * included, so the two moments are announced rather than inferred. The current state is delivered
   * on subscribing.
   */
  onLink(listener: (state: LinkState) => void): () => void;
  /** Who is in which seat. Null until a session is open. */
  seats(): SeatDirectory | null;
  /**
   * Copies every transport event to `listeners` as well as to the lobby. Returns a function that
   * stops the copy, which is what a mode calls when it is torn down.
   */
  relayTo(listeners: TransportListeners): () => void;
}

export function createLobbySession(
  open: TransportFactory = openTrysteroTransport,
  clock: LobbySessionClock = BROWSER_LOBBY_CLOCK,
  tokens: CarriedToken = createCarriedToken(),
): LobbySession {
  let state: LobbyState = EMPTY_LOBBY;
  let transport: Transport | null = null;
  let host: LobbyHost | null = null;
  let guest: LobbyGuest | null = null;
  let stopSide: (() => void) | null = null;
  /** The code this session is on, and the token a guest gets its own seat back with. */
  let sessionCode: string | null = null;
  let carried: string | null = null;
  /** What this machine calls itself. Null until a surface has rolled one. */
  let playerName: string | null = null;
  /** Who this machine is playing as. Null until a surface has read it off the profile. */
  let playerSheet: CharacterSheet | null = null;
  let rejoining: AbortController | null = null;
  const relays = new Set<TransportListeners>();
  const linkListeners = new Set<(state: LinkState) => void>();
  let link: LinkState = 'lost';
  const relay = fanOut(relays);
  const listeners = new Set<(state: LobbyState) => void>();
  const noticeListeners = new Set<(notice: SessionNotice) => void>();

  const notify = (notice: SessionNotice): void => {
    for (const listener of [...noticeListeners]) listener(notice);
  };

  /** Said once per change, so nothing is told twice that its connection went or came back. */
  const announceLink = (next: LinkState): void => {
    if (next === link) return;
    link = next;
    for (const listener of [...linkListeners]) listener(link);
  };

  const apply = (event: LobbyEvent): void => {
    const next = reduceLobby(state, event);
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener(state);
  };

  /** One attempt at a connection. Rejects the way opening always does, with a `NetError`. */
  const openOnce = (role: SessionRole, code: string | null): Promise<Transport> =>
    open({
      code,
      role,
      // Read at handshake time, long after this call. A seat kept for somebody who dropped has no
      // peer to count, and it is still occupied.
      heldTokens: (): readonly string[] => host?.heldTokens() ?? [],
      // What this tab was seated with here before, so the door can tell a returning player from a
      // fifth one. Set by `start` from the record kept under this code.
      rejoinToken: (): string | null => carried,
      listeners: {
        onStatus: (status): void => {
          apply({ kind: 'status', status });
          relay.onStatus(status);
        },
        onPeerJoin: (peer): void => {
          apply({ kind: 'peer', peer });
          relay.onPeerJoin(peer);
        },
        onPeerUpdate: (peer): void => {
          apply({ kind: 'peer', peer });
          relay.onPeerUpdate(peer);
        },
        onPeerLeave: (id, reason): void => {
          apply({ kind: 'peerLeft', id });
          relay.onPeerLeave(id, reason);
        },
        onMessage: (from, frame, channel): void => {
          relay.onMessage(from, frame, channel);
        },
        onError: (error: NetError): void => {
          apply({ kind: 'note', text: error.message });
          relay.onError(error);
        },
      },
    });

  const failed = (error: NetError): void => {
    apply({
      kind: 'status',
      status: { phase: 'failed', detail: error.message, code: error.code, strategyId: null },
    });
  };

  const adopt = (opened: Transport): void => {
    transport = opened;
    sessionCode = opened.joinCode;
    apply({
      kind: 'opened',
      code: opened.joinCode,
      role: opened.role,
      selfId: opened.selfId,
    });
    // The side is built after the state says a session exists, so the roster it publishes on the
    // way up lands on a lobby that already knows what it is looking at.
    openSide(opened);
    apply({ kind: 'status', status: opened.status() });
    // Last, so anything that starts sending the moment it hears this is sending over a session
    // whose own half is already listening.
    announceLink('live');
  };

  const start = async (role: SessionRole, code: string | null): Promise<void> => {
    await stop();
    // A guest dialling a code it was seated on before is the same player coming back, whether it
    // dropped a second ago or reloaded the page. Anything else starts with nothing to present: a
    // host holds no token of its own, and a code this tab has never sat on has no record.
    carried = role === 'guest' && code !== null ? tokens.recall(code) : null;
    apply({ kind: 'opening', role });

    try {
      adopt(await openOnce(role, code));
    } catch (cause) {
      failed(asNetError(cause, 'peerConnectionFailed'));
    }
  };

  /** Whichever half of the lobby protocol this machine speaks, relayed the same events as anyone */
  function openSide(opened: Transport): void {
    if (opened.role === 'host') {
      host = createLobbyHost({
        transport: opened,
        nowMs: clock.nowMs,
        repeat: clock.repeat,
        onEvent: apply,
        onNotice: notify,
        mintToken: mintRejoinToken,
        playerName: playerName ?? undefined,
        playerSheet: playerSheet ?? undefined,
      });
      stopSide = relaySide(host.listeners);
      return;
    }

    guest = createLobbyGuest({
      transport: opened,
      onEvent: apply,
      onNotice: notify,
      playerName: playerName ?? undefined,
      playerSheet: playerSheet ?? undefined,
      // Written down the moment the host says it, so the seat survives this page as well as this
      // connection. The code comes from the transport rather than from what was typed, because
      // that is the one this session actually landed on.
      onToken: (token: string): void => {
        tokens.keep(opened.joinCode, token);
      },
    });
    stopSide = relaySide(guest.listeners);
    guest.announceArrival();
    // The host may already have been accepted by the time this connection resolved, in which case
    // its arrival has been and gone and there is nothing left to hang the token on.
    if (carried !== null) {
      guest.adoptToken(carried);
      guest.presentToken();
    }
  }

  function relaySide(sideListeners: TransportListeners): () => void {
    relays.add(sideListeners);
    return (): void => {
      relays.delete(sideListeners);
    };
  }

  function closeSide(): void {
    stopSide?.();
    stopSide = null;
    host?.dispose();
    host = null;
    // Kept, because the whole point of a token is that it outlives the connection it arrived on.
    carried = guest?.token() ?? carried;
    guest = null;
  }

  /** Everything that has to stop before this machine can be somewhere else. */
  const stop = async (): Promise<void> => {
    rejoining?.abort();
    rejoining = null;
    const open = transport;
    transport = null;
    announceLink('lost');
    if (open === null) {
      closeSide();
      return;
    }

    // Said before the connection goes, so nobody has to wait out a health timeout to learn the
    // session ended on purpose.
    host?.announceEnding();
    guest?.announceLeaving();
    // A player who leaves has given the seat up rather than lost it, so the token goes with it.
    // Keeping it would have the next join on this code present a token to a seat that was released
    // the moment this one said goodbye.
    if (guest !== null && sessionCode !== null) tokens.forget(sessionCode);
    closeSide();
    await open.close('left the lobby');
    apply({ kind: 'left' });
  };

  return {
    state: (): LobbyState => state,

    subscribe(listener: (state: LobbyState) => void): () => void {
      listeners.add(listener);
      listener(state);
      return (): void => {
        listeners.delete(listener);
      };
    },

    onNotice(listener: (notice: SessionNotice) => void): () => void {
      noticeListeners.add(listener);
      return (): void => {
        noticeListeners.delete(listener);
      };
    },

    relayTo(next: TransportListeners): () => void {
      relays.add(next);
      return (): void => {
        relays.delete(next);
      };
    },

    onLink(listener: (state: LinkState) => void): () => void {
      linkListeners.add(listener);
      listener(link);
      return (): void => {
        linkListeners.delete(listener);
      };
    },

    setReady(ready: boolean): void {
      guest?.setReady(ready);
    },

    setName(name: string): void {
      if (name === playerName) return;
      playerName = name;
      host?.setName(name);
      guest?.setName(name);
    },

    setSheet(sheet: CharacterSheet): void {
      playerSheet = sheet;
      host?.setSheet(sheet);
      guest?.setSheet(sheet);
    },

    setRules(rules: SessionRules): void {
      host?.setRules(rules);
    },

    async rejoin(): Promise<void> {
      const code = sessionCode;
      if (code === null || state.role !== 'guest') return;

      // The dead connection goes first, and its token is kept: this is the same player coming back
      // to the same seat, not somebody joining a session they were never in.
      const open = transport;
      transport = null;
      announceLink('lost');
      closeSide();
      await open?.close('lost the host');

      rejoining = new AbortController();
      const outcome = await runRejoinAttempt({
        budgetMs: SEAT_HOLD_MS,
        open: (): Promise<Transport> => openOnce('guest', code),
        nowMs: clock.nowMs,
        sleep: clock.sleep,
        signal: rejoining.signal,
        onWaiting: (msLeft): void => {
          apply({ kind: 'rejoining', msLeft });
        },
      });
      rejoining = null;
      apply({ kind: 'rejoining', msLeft: null });

      if (outcome.ok) {
        adopt(outcome.transport);
        return;
      }
      if (outcome.error.code === 'hostLeft') apply({ kind: 'ending', reason: 'hostLeft' });
      failed(outcome.error);
    },

    cancelRejoin(): void {
      rejoining?.abort();
      rejoining = null;
    },

    host: (code: string | null = null): Promise<void> => start('host', code),
    join: (code: string): Promise<void> => start('guest', code),
    leave: stop,
    transport: (): Transport | null => transport,
    hostId: (): PeerId | null => {
      if (transport === null) return null;
      if (transport.role === 'host') return transport.selfId;
      return transport.peers().find((peer) => peer.role === 'host')?.id ?? null;
    },
    seats: (): SeatDirectory | null => host?.seats ?? null,
  };
}

/**
 * Every relay in the set, as one `TransportListeners`.
 *
 * Copied before each fan-out, because a mode that stops itself from inside `onPeerLeave` removes
 * its own relay while this is walking the set.
 */
function fanOut(relays: ReadonlySet<TransportListeners>): TransportListeners {
  return {
    onStatus: (status): void => {
      for (const relay of [...relays]) relay.onStatus(status);
    },
    onPeerJoin: (peer): void => {
      for (const relay of [...relays]) relay.onPeerJoin(peer);
    },
    onPeerUpdate: (peer): void => {
      for (const relay of [...relays]) relay.onPeerUpdate(peer);
    },
    onPeerLeave: (id, reason): void => {
      for (const relay of [...relays]) relay.onPeerLeave(id, reason);
    },
    onMessage: (from, frame, channel): void => {
      for (const relay of [...relays]) relay.onMessage(from, frame, channel);
    },
    onError: (error): void => {
      for (const relay of [...relays]) relay.onError(error);
    },
  };
}
